import { alpha, Button, Stack, styled } from '@mui/material';
import { getNavFloatShadow } from 'src/components/Navbar/DesktopDrawer/desktop-drawer.styles';
import { GLASS_BLUR, SUPPORTS_BACKDROP_FILTER } from 'src/constants/glass';

export type FloatingStripTone = 'base' | 'raised';

export type FloatingStripIconTone = 'note' | 'accent';

/** The glass group the folded composer strip, the bulk selection bar and the bulk job banners float in. */
export const FloatingStripGroup = styled(Stack, {
  name: 'FloatingStripGroup',
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone?: FloatingStripTone }>(({ theme, tone = 'base' }) => {
  const { sys } = theme.palette.custom.OV;
  const isRaised = tone === 'raised';

  return {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.125rem',
    padding: '0.25rem',
    borderRadius: '0.75rem',
    border: `1px solid ${alpha(sys.outlineVariant, 0.75)}`,
    backgroundColor: isRaised
      ? alpha(sys.surfaceContainer, 0.82)
      : alpha(sys.surfaceContainerLowest, 0.78),
    backdropFilter: GLASS_BLUR,
    WebkitBackdropFilter: GLASS_BLUR,
    boxShadow: getNavFloatShadow(theme),
    [SUPPORTS_BACKDROP_FILTER]: {
      backgroundColor: isRaised ? sys.surfaceContainerHigh : sys.surfaceBright,
    },
  };
});

export const FloatingStripButton = styled(Button, {
  name: 'FloatingStripButton',
  shouldForwardProp: (prop) => prop !== 'iconTone' && prop !== 'iconOnly',
})<{ iconTone?: FloatingStripIconTone; iconOnly?: boolean }>(({
  theme,
  iconTone,
  iconOnly,
}) => {
  const { sys, extended } = theme.palette.custom.OV;
  const iconColorByTone: Record<FloatingStripIconTone, string> = {
    note: extended.Signature.color,
    accent: sys.secondary,
  };

  return {
    gap: '0.4375rem',
    padding: '0.5rem 0.75rem',
    borderRadius: '0.5625rem',
    fontSize: '0.78125rem',
    fontWeight: 600,
    lineHeight: 1.2,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    minWidth: 0,
    color: sys.onBackground,
    '& .MuiButton-startIcon': {
      margin: 0,
      color: iconTone ? iconColorByTone[iconTone] : sys.onSurfaceVariant,
    },
    '&:hover': { backgroundColor: sys.surfaceContainerHigh },
    ...(iconOnly && {
      padding: '0.5rem',
      '& > :not(.MuiButton-startIcon)': { display: 'none' },
    }),
  };
});
